import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  QButton,
  QCenter,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';

import { documentApi } from '../../../../../api/document';

import { CredentialConfirmationModal } from '../../../CredentialConfirmationModal/index';
import { useTrainingAssessment } from '../../../TrainingAssessment/hooks/useTrainingAssessment';
import { TrainingAssessment } from '../../../TrainingAssessment/types/trainingAssessment';

import { DocumentOverviewContext } from '../../Context';
import { shouldRenderCompleteTraining } from '../../RenderLogic';
import { useNavigateAfterTraining } from '../hooks';

const hasTrainingAssessment = (trainingAssessment?: TrainingAssessment) => {
  if (!trainingAssessment) {
    return false;
  } else {
    return Boolean(trainingAssessment?.questions?.length);
  }
};

export const DocumentTrainingButton = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();
  const { id } = useParams();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const navigateAfterTraining = useNavigateAfterTraining();
  const { showToast } = useToastProvider();
  const { qualioDocument, currentEditorMode } = useContext(
    DocumentOverviewContext,
  );
  const {
    data: trainingAssessment,
    isLoading,
    isError,
  } = useTrainingAssessment(qualioDocument.id);

  const handleStartTrainingAssessment = () => {
    const idPath = `/${id}`;
    const basePath = location.pathname.split(idPath)[0] + idPath;
    void navigate(`${basePath}/training/assessment`);
  };

  if (
    !shouldRenderCompleteTraining(
      qualioDocument,
      currentUser,
      currentEditorMode,
    )
  ) {
    return null;
  }

  const handleTraining = () => {
    if (!hasTrainingAssessment(trainingAssessment)) {
      setIsModalOpen(true);
    } else {
      handleStartTrainingAssessment();
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleConfirmTraining = async (email: string, password: string) => {
    const emptyAssessment = !trainingAssessment
      ? undefined
      : {
          documentId: qualioDocument.id,
          questions: trainingAssessment.questions,
          id: trainingAssessment.id,
        };
    try {
      await documentApi.signOffTraining(
        qualioDocument.id,
        email,
        password,
        emptyAssessment,
      );
      showToast({
        'data-cy': 'training-sign-off-success-toast',
        id: 'training-sign-off-success',
        description: t('notifications.successfullyCompletedTraining'),
        status: 'success',
        title: t('notifications.trainingComplete'),
        replace: true,
      });
      navigateAfterTraining();
    } catch (error: unknown) {
      throw error;
    }
  };

  const handleSignOffError = () => {
    showToast({
      'data-cy': 'training-assessment-sign-off-error-toast',
      id: 'training-assessment-sign-off-error',
      title: t('notifications.error'),
      description: t('notifications.couldNotSignOffAssessment'),
      status: 'error',
      replace: true,
    });
  };

  const buttonText = hasTrainingAssessment(trainingAssessment)
    ? t('buttons.startTrainingAssessment')
    : t('modals.completeTraining');

  return (
    <>
      <QCenter mt={6}>
        <QButton
          isLoading={isLoading}
          isDisabled={isError}
          variant="solid"
          onClick={handleTraining}
        >
          {buttonText}
        </QButton>
      </QCenter>
      <CredentialConfirmationModal
        isOpen={isModalOpen}
        onCloseModal={handleCloseModal}
        handleStatusChange={handleConfirmTraining}
        modalHeading={t('modals.completeTraining')}
        modalText={t('modals.enterDigitalSignatureTraining')}
        buttonActionLabel={t('buttons.signOff')}
        withComment={false}
        handleError={handleSignOffError}
      />
    </>
  );
};
